import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
    IoArrowForward, IoTrendingUp, IoShield, IoRocket, 
    IoSearch, IoCart, IoFlash, IoTrophy, IoSparkles, IoCheckmark
} from 'react-icons/io5'

import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import ProductCard from '@/components/common/ProductCard/ProductCard'
import CategoryCard from '@/components/common/CategoryCard/CategoryCard'
import { useProducts, useCategories } from '@/hooks/useProducts'
import { sampleProductImage, sampleCategoryImage } from '@/utils/constants/common'

import './Home.css'

const Home = () => {
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState('')
    
    const { data: products, isLoading: productsLoading } = useProducts({ page_size: 12 })
    const { data: categories, isLoading: categoriesLoading } = useCategories()
    

    const featuredCategories = categories?.slice(0, 12) || []
    const featuredProducts = products?.results?.slice(0, 12) || []

    const handleCategoryClick = (categoryId: number) => {
        navigate(`/products?category=${categoryId}`)
    }

    const handleSearch = () => {
        if (searchQuery.trim()) {
            navigate(`/products?search=${encodeURIComponent(searchQuery)}`)
        }
    }

    return (
        <div className="home_main_container">
            {/* Hero Section */}
            <section className="home_hero_section">
                <div className="home_hero_background"></div>
                <div className="home_hero_content">
                    <div className="home_hero_text_area">
                        <div className="home_hero_badge">
                            <IoSparkles size={16} />
                            <span>Nueva Colección Disponible</span>
                        </div>
                        <h1 className="home_hero_title">
                            Productos Plásticos <span className="home_gradient_text">Premium</span> para Cada Necesidad
                        </h1>
                        <p className="home_hero_subtitle">
                            Descubre nuestra amplia colección de productos plásticos de alta calidad. 
                            Desde contenedores personalizados hasta soluciones industriales - tenemos todo lo que necesitas.
                        </p>
                        
                        {/* Search Bar */}
                        <div className="home_hero_search">
                            <div className="home_search_container">
                                <IoSearch className="home_search_icon" />
                                <input
                                    type="text"
                                    className="home_search_input"
                                    placeholder="Buscar productos, categorías o marcas..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                                <Button variant="primary" onClick={handleSearch}>
                                    Buscar
                                </Button>
                            </div>

                            <div className="home_hero_actions">
                                <Button 
                                    variant="primary" 
                                    size="lg"
                                    onClick={() => navigate('/products')}
                                >
                                    <IoCart size={20} />
                                    Comprar Ahora
                                </Button>
                                <Button 
                                    variant="secondary" 
                                    size="lg"
                                    onClick={() => navigate('/categories')}
                                >
                                    <IoTrophy size={20} />
                                    Ver Categorías
                                </Button>
                            </div>
                        </div>
                    </div>
                    
                    <div className="home_hero_visual">
                        <div className="home_product_showcase">
                            <div className="home_showcase_item home_showcase_primary">
                                <img src={sampleProductImage} alt="Producto Destacado" />
                                <div className="home_showcase_badge">
                                    <IoTrophy size={14} />
                                    <span>Más Vendido</span>
                                </div>
                            </div>
                            <div className="home_showcase_item home_showcase_secondary">
                                <img src={sampleCategoryImage} alt="Producto Premium" />
                                <div className="home_showcase_badge">
                                    <IoSparkles size={14} />
                                    <span>Nuevo</span>
                                </div>
                            </div>
                            <div className="home_floating_elements">
                                <div className="home_floating_card home_card_quality">
                                    <IoShield size={24} />
                                    <span>Calidad Premium</span>
                                </div>
                                <div className="home_floating_card home_card_shipping">
                                    <IoFlash size={24} />
                                    <span>Envío Rápido</span>
                                </div>
                                <div className="home_floating_card home_card_custom">
                                    <IoRocket size={24} />
                                    <span>Personalizable</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="home_features_section">
                <div className="home_features_container">
                    <div className="home_features_header">
                        <div className="home_features_badge">
                            <IoShield size={16} />
                            <span>Nuestras Ventajas</span>
                        </div>
                        <h2 className="home_features_title">¿Por Qué Elegir Plastitex?</h2>
                        <p className="home_features_subtitle">
                            Descubre las razones que nos convierten en líderes del mercado
                        </p>
                    </div>
                    <div className="home_features_grid">
                        <div className="home_feature_card">
                            <div className="home_feature_icon">
                                <IoShield size={48} />
                            </div>
                            <h3 className="home_feature_title">Calidad Premium</h3>
                            <p className="home_feature_description">
                                Todos nuestros productos están fabricados con materiales de alta calidad garantizando durabilidad y confiabilidad.
                            </p>
                        </div>
                        <div className="home_feature_card">
                            <div className="home_feature_icon">
                                <IoCheckmark size={48} />
                            </div>
                            <h3 className="home_feature_title">Soluciones Personalizadas</h3>
                            <p className="home_feature_description">
                                Personaliza tus productos con nuestras opciones avanzadas de personalización y herramientas de diseño.
                            </p>
                        </div>
                        <div className="home_feature_card">
                            <div className="home_feature_icon">
                                <IoTrendingUp size={48} />
                            </div>
                            <h3 className="home_feature_title">Entrega Rápida</h3>
                            <p className="home_feature_description">
                                Procesamiento rápido y envío para obtener tus productos cuando más los necesites.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Categories Section */}
            <section className="home_categories_section">
                <div className="home_categories_container">
                    <div className="home_categories_header">
                        <div className="home_categories_badge">
                            <IoTrendingUp size={16} />
                            <span>Categorías Populares</span>
                        </div>
                        <h2 className="home_categories_title">Comprar por Categoría</h2>
                        <p className="home_categories_subtitle">
                            Descubre nuestras categorías de productos más populares
                        </p>
                    </div>

                    {categoriesLoading ? (
                        <div className="home_categories_loading">
                            <Spinner size="md" />
                        </div>
                    ) : (
                        <div className="home_categories_grid">
                            {featuredCategories.map((category, index) => (
                                <CategoryCard
                                    key={category.id}
                                    category={category}
                                    variant="simple"
                                    showStats={true}
                                    showOverlay={true}
                                    animationDelay={index * 0.1}
                                    onClick={handleCategoryClick}
                                />
                            ))}
                        </div>
                    )}
                    
                    <div className="home_categories_cta">
                        <Button 
                            variant="secondary" 
                            size="lg"
                            onClick={() => navigate('/categories')}
                        >
                            Ver Todas las Categorías <IoArrowForward />
                        </Button>
                    </div>
                </div>
            </section>

            {/* Featured Products Section */}
            <section className="home_products_section">
                <div className="home_products_container">
                    <div className="home_products_header">
                        <div className="home_products_badge">
                            <IoFlash size={16} />
                            <span>Tendencia Ahora</span>
                        </div>
                        <h2 className="home_products_title">Productos Destacados</h2>
                        <p className="home_products_subtitle">
                            Productos seleccionados a mano que están en tendencia en nuestro mercado
                        </p>
                    </div>

                    {productsLoading ? (
                        <div className="home_products_loading">
                            <Spinner size="md" />
                        </div>
                    ) : (
                        <div className="home_products_grid">
                            {featuredProducts.map((product, index) => (
                                <ProductCard
                                    key={product.id}
                                    product={product}
                                    variant="default"
                                    showDescription={true}
                                    showFeatures={true}
                                    showActions={true}
                                    animationDelay={index * 0.1}
                                />
                            ))}
                        </div>
                    )}
                    
                    <div className="home_products_cta">
                        <Button 
                            variant="secondary" 
                            size="lg"
                            onClick={() => navigate('/products')}
                        >
                            Ver Todos los Productos <IoArrowForward />
                        </Button>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="home_stats_section">
                <div className="home_stats_container">
                    <div className="home_stats_grid">
                        <div className="home_stat_item">
                            <div className="home_stat_icon">
                                <IoTrophy size={32} />
                            </div>
                            <div className="home_stat_content">
                                <span className="home_stat_number">50K+</span>
                                <span className="home_stat_label">Clientes Satisfechos</span>
                            </div>
                        </div>
                        <div className="home_stat_item">
                            <div className="home_stat_icon">
                                <IoFlash size={32} />
                            </div>
                            <div className="home_stat_content">
                                <span className="home_stat_number">1000+</span>
                                <span className="home_stat_label">Productos Disponibles</span>
                            </div>
                        </div>
                        <div className="home_stat_item">
                            <div className="home_stat_icon">
                                <IoCheckmark size={32} />
                            </div>
                            <div className="home_stat_content">
                                <span className="home_stat_number">99%</span>
                                <span className="home_stat_label">Calidad Garantizada</span>
                            </div>
                        </div>
                        <div className="home_stat_item">
                            <div className="home_stat_icon">
                                <IoRocket size={32} />
                            </div>
                            <div className="home_stat_content">
                                <span className="home_stat_number">24H</span>
                                <span className="home_stat_label">Envío Express</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default Home